import { useRef } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Image, Text } from '@frontend/design-system';
import { FormWrapper } from '../form-wrapper';

type LogoUploaderProps = {
  logoUrl?: string;
  onLogoSelect: (file: File) => void;
  onLogoDelete: () => void;
};

export const LogoUploader = ({ logoUrl, onLogoSelect, onLogoDelete }: LogoUploaderProps) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onLogoSelect(file);
    }
    // Reset so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <FormWrapper.Field>
      <Text weight='bold'>{t('Logo')}</Text>
      <input
        type='file'
        accept='image/png, image/jpeg, image/jpg'
        ref={fileInputRef}
        onChange={handleFileChange}
        hidden
      />
      {logoUrl ? (
        <>
          <div css={logoPreviewContainerStyle}>
            <Image src={logoUrl} alt={t('Logo Preview')} css={logoImageStyle} />
          </div>
          <div css={buttonBarStyle}>
            <Button variant='secondary' onClick={handleClick}>
              {t('Select New Image')}
            </Button>
            <Button variant='secondary' destructive onClick={onLogoDelete}>
              {t('Delete Logo')}
            </Button>
          </div>
        </>
      ) : (
        <Button variant='secondary' iconName='upload' onClick={handleClick} css={uploadButtonStyle}>
          {t('Upload Logo')}
        </Button>
      )}
    </FormWrapper.Field>
  );
};

const logoPreviewContainerStyle = css`
  width: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${theme.colors.neutral5};
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.neutral20};
  padding: ${theme.spacing(2)};
`;

const logoImageStyle = css`
  max-height: 300px;
  max-width: 100%;
  object-fit: contain;
`;

const buttonBarStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
`;

const uploadButtonStyle = css`
  width: fit-content;
`;
